// Other writers' carets and selections (W9b; PROTOCOL.md §10.12): a caret with a small name flag in each member's
// colour, their selection tinted. The app resolves each member's position (sent as scalars at their heads) to UTF-16
// offsets in a fragment's text now and hands them over with `setRemoteCarets`; from then on the editor maps them through
// every change — its own writer's typing and other devices' text arriving alike — so a caret stays on its words while
// text comes in around it. A member whose newest position can't be resolved yet (their text is on its way) is `kept`:
// their caret stays where it was, mapped. A caret is never drawn inside a run of hidden markers (`*`, `**`, a span's
// brackets): it's drawn at the run's end, where the editor's own caret would rest (`filter.caretRule`).
import { StateEffect, StateField, type EditorState, type Extension, type Range, type Transaction } from '@codemirror/state';
import { Decoration, EditorView, WidgetType, type DecorationSet } from '@codemirror/view';
import { hiddenAt } from './filter.ts';
import { fragmentAt } from './session.ts';
import { layoutField, layoutOf } from './state.ts';

export interface RemoteCaret {
  /** Who (one caret per key: a member's device). */
  key: string;
  name: string;
  /** A CSS colour. */
  color: string;
  fragmentId: string;
  /** The selection in UTF-16 offsets of the fragment's text as the editor has it now; head is the caret. */
  anchor: number;
  head: number;
  /** Typing in the last moments (the flag says so). */
  typing?: boolean;
}

/** New carets: `set` placed afresh (their offsets are the text now), `keep` left where they are mapped to (their
 *  names, colours and typing updated when given in `update`), every other caret removed. */
export interface RemoteCaretsSpec {
  set: RemoteCaret[];
  keep?: string[];
  update?: Pick<RemoteCaret, 'key' | 'name' | 'color' | 'typing'>[];
}
export const setRemoteCarets = StateEffect.define<RemoteCaretsSpec>();

interface Placed {
  key: string;
  name: string;
  color: string;
  typing: boolean;
  fragmentId: string;
  /** Document positions. */
  anchor: number;
  head: number;
}

function place(state: EditorState, c: RemoteCaret): Placed | null {
  const layout = layoutOf(state);
  const i = layout.index(c.fragmentId);
  if (i === undefined) return null;
  const length = layout.to[i] - layout.from[i];
  const at = (n: number) => layout.from[i] + Math.max(0, Math.min(length, Math.round(n)));
  return { key: c.key, name: c.name, color: c.color, typing: !!c.typing, fragmentId: c.fragmentId, anchor: at(c.anchor), head: at(c.head) };
}

/** A caret carried through a change: one at a point follows text typed there (it's most often its own writer's); a
 *  selection doesn't grow at its edges. Kept inside its fragment. */
function mapped(p: Placed, tr: Transaction): Placed | null {
  const layout = tr.state.field(layoutField);
  const i = layout.index(p.fragmentId);
  if (i === undefined) return null;
  let anchor: number, head: number;
  if (p.anchor === p.head) anchor = head = tr.changes.mapPos(p.head, 1);
  else {
    const forward = p.anchor < p.head;
    const [a, b] = forward ? [p.anchor, p.head] : [p.head, p.anchor];
    const from = tr.changes.mapPos(a, 1), to = Math.max(from, tr.changes.mapPos(b, -1));
    [anchor, head] = forward ? [from, to] : [to, from];
  }
  const clamp = (n: number) => Math.max(layout.from[i], Math.min(layout.to[i], n));
  return { ...p, anchor: clamp(anchor), head: clamp(head) };
}

export const remoteCaretField = StateField.define<Placed[]>({
  create: () => [],
  update(carets, tr) {
    let next = carets;
    if (tr.docChanged) next = next.flatMap((p) => mapped(p, tr) ?? []);
    for (const e of tr.effects) {
      if (!e.is(setRemoteCarets)) continue;
      const spec = e.value;
      const keep = new Set(spec.keep ?? []);
      const updates = new Map((spec.update ?? []).map((u) => [u.key, u]));
      const kept = next
        .filter((p) => keep.has(p.key) && !spec.set.some((c) => c.key === p.key))
        .map((p) => {
          const u = updates.get(p.key);
          return u ? { ...p, name: u.name, color: u.color, typing: !!u.typing } : p;
        });
      next = [...kept, ...spec.set.flatMap((c) => place(tr.state, c) ?? [])];
    }
    return next;
  },
});

class CaretWidget extends WidgetType {
  constructor(readonly key: string, readonly name: string, readonly color: string, readonly typing: boolean) { super(); }
  eq(o: CaretWidget) { return o.key === this.key && o.name === this.name && o.color === this.color && o.typing === this.typing; }
  toDOM() {
    const caret = document.createElement('span');
    caret.className = 'ws-remote-caret' + (this.typing ? ' ws-remote-typing' : '');
    caret.style.setProperty('--ws-remote', this.color);
    caret.setAttribute('data-remote-caret', this.key);
    caret.setAttribute('data-remote-name', this.name);
    caret.setAttribute('aria-hidden', 'true');
    const flag = caret.appendChild(document.createElement('span'));
    flag.className = 'ws-remote-flag';
    flag.textContent = this.name;
    return caret;
  }
  ignoreEvent() { return true; }
}

/** Where a caret is drawn: never inside a run of hidden markers (moved to its end), never outside its fragment. */
function drawnAt(state: EditorState, pos: number, hidden: (i: number) => boolean): number {
  let p = pos;
  if (hidden(p - 1) && hidden(p)) while (hidden(p)) p++;
  return p;
}

function decorations(state: EditorState): DecorationSet {
  const carets = state.field(remoteCaretField);
  if (!carets.length) return Decoration.none;
  const hidden = hiddenAt(state);
  const out: Range<Decoration>[] = [];
  for (const c of carets) {
    const from = Math.min(c.anchor, c.head), to = Math.max(c.anchor, c.head);
    if (to > from) {
      out.push(Decoration.mark({ class: 'ws-remote-selection', attributes: { style: `--ws-remote: ${c.color}`, 'data-remote-selection': c.key } }).range(from, to));
    }
    out.push(Decoration.widget({ widget: new CaretWidget(c.key, c.name, c.color, c.typing), side: 1 }).range(drawnAt(state, c.head, hidden)));
  }
  return Decoration.set(out, true);
}

/** The field, its decorations, and nothing else: the app's extension sets the carets. */
export function remoteCarets(): Extension {
  return [remoteCaretField, EditorView.decorations.compute([remoteCaretField, 'doc'], decorations)];
}

/** The carets as they stand now, in each fragment's UTF-16 offsets, drawn where they're drawn (tests, the app). */
export function remoteCaretPositions(state: EditorState): { key: string; name: string; fragmentId: string; anchor: number; head: number; drawnAt: number }[] {
  const layout = layoutOf(state);
  const hidden = hiddenAt(state);
  return state.field(remoteCaretField).flatMap((c) => {
    const i = layout.index(c.fragmentId);
    if (i === undefined) return [];
    const base = layout.from[i];
    return [{ key: c.key, name: c.name, fragmentId: c.fragmentId, anchor: c.anchor - base, head: c.head - base, drawnAt: drawnAt(state, c.head, hidden) - base }];
  });
}

/** This editor's own selection as a member's presence carries it: the fragment and both ends (UTF-16), or null in a
 *  divider or across fragments. */
export function localSelection(state: EditorState): { fragmentId: string; anchor: number; head: number } | null {
  const r = state.selection.main;
  const head = fragmentAt(state, r.head);
  if (!head) return null;
  const anchor = fragmentAt(state, r.anchor);
  return { fragmentId: head.fragmentId, head: head.offset, anchor: anchor && anchor.fragmentId === head.fragmentId ? anchor.offset : head.offset };
}

/** The fragments the editor shows, in order. */
export const shownFragmentIds = (state: EditorState): string[] => layoutOf(state).pieces.map((p) => p.id);

/** A shown fragment's text as the editor has it now, or null when it isn't shown. */
export function fragmentBody(state: EditorState, fragmentId: string): string | null {
  const layout = layoutOf(state);
  const i = layout.index(fragmentId);
  return i === undefined ? null : state.doc.sliceString(layout.from[i], layout.to[i]);
}
